import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  RefreshCw,
  Edit,
  Trash2,
  Shield,
  Mail,
  Users,
  CheckCircle,
  Eye,
  UserPlus,
  Crown,
  Building2,
  Wifi,
  Clock,
  ArrowLeftRight,
  KeyRound,
  Send,
  Unlock,
  FlaskConical,
  Network,
} from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import Tabs from '../../components/common/Tabs';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { USER_TYPES } from '../../utils/constants';
import StatCard from '../../components/common/StatCard';
import UserModal from '../../components/admin/UserModal';
import InviteUsersModal, { type InviteData } from '../../components/admin/InviteUsersModal';
import InviteVPNUsersModal, { type InviteData as VPNInviteData } from '../../components/admin/InviteVPNUsersModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SetPasswordModal from '../../components/admin/SetPasswordModal';
import AddDemoUserModal from '../../components/admin/AddDemoUserModal';
import VPNUsersTab from '../../components/admin/VPNUsersTab';
import vpnUserService from '../../services/vpnUser.service';
import PendingInvitationsTab from '../../components/admin/PendingInvitationsTab';
import { userService } from '../../services';
import type { User as ApiUser } from '../../services';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { useToast } from '../../components/common/ToastContainer';
import { usePermissions } from '../../hooks/usePermissions';
import { authService } from '../../services/auth.service';

interface UserRecord {
  id: string;
  username: string;
  email: string;
  fullName: string;
  phone: string;
  role: 'SuperAdmin' | 'ChannelAdmin' | 'TenantAdmin' | 'User';
  type: number;
  tenantName: string;
  status: 'Active' | 'Inactive';
  statusValue: number;
  lastLogin: string;
  createTime: string;
}

const UsersAdmin = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const { user: currentUser } = useAuthStore();
  const { tenants, selectedTenantId, getSelectedTenant } = useTenantStore();
  const isSuperAdmin = currentUser?.type === USER_TYPES.SUPER_ADMIN;
  const isChannelAdmin = currentUser?.type === USER_TYPES.CHANNEL_ADMIN;
  // Both types have no tenant of their own and drive this page off the topbar
  // tenant selection instead of an implicit one.
  const isGlobalScoped = isSuperAdmin || isChannelAdmin;
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [vpnUserCount, setVpnUserCount] = useState<number | null>(null);
  const [page] = useState(1);
  const [pageSize] = useState(100); // Fetch more to filter client-side

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isVPNInviteModalOpen, setIsVPNInviteModalOpen] = useState(false);
  const [isDemoUserModalOpen, setIsDemoUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | undefined>();
  const [deleteUser, setDeleteUser] = useState<UserRecord | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  // Native-auth admin actions
  const [setPwdUser, setSetPwdUser] = useState<UserRecord | undefined>();
  const [resetEmailUser, setResetEmailUser] = useState<UserRecord | undefined>();
  const [unlockUser, setUnlockUser] = useState<UserRecord | undefined>();
  const [lockedUserIds, setLockedUserIds] = useState<Set<string>>(new Set());

  // Assume identity state
  const [assumeEnabled, setAssumeEnabled] = useState(false);
  const [assumeTarget, setAssumeTarget] = useState<UserRecord | undefined>();
  const [isAssuming, setIsAssuming] = useState(false);
  const { assumeIdentity: storeAssumeIdentity } = useAuthStore();

  // Get selected tenant info for invite modal
  const selectedTenant = getSelectedTenant();

  // Track which tab is active in the SuperAdmin view so we can enable/disable
  // the Invite button per tab. super-admins is always enabled; tenant-admins
  // and vpn-users require a tenant selected in the topbar.
  const [activeSuperAdminTab, setActiveSuperAdminTab] = useState<string>('tenant-admins');
  const [activeTenantTab, setActiveTenantTab] = useState<string>('admins');

  // A ChannelAdmin belongs to no tenant and is scoped by channel instead. The
  // invite modal collects the owning channel and the API creates the
  // membership up front, so no tenant is needed here at all. The button is
  // always available on the channel-admins tab; the modal handles the
  // "no channels exist yet" case.
  const canInvite = isSuperAdmin
    ? activeSuperAdminTab === 'super-admins' ||
      activeSuperAdminTab === 'channel-admins' ||
      !!selectedTenantId
    : true;

  // Check if assume identity is enabled
  useEffect(() => {
    if (isSuperAdmin) {
      authService.checkAssumeEnabled()
        .then(({ enabled }) => setAssumeEnabled(enabled))
        .catch(() => setAssumeEnabled(false));
    }
  }, [isSuperAdmin]);

  const handleAssumeConfirm = async () => {
    if (!assumeTarget) return;
    try {
      setIsAssuming(true);
      const result = await authService.assumeIdentity(assumeTarget.id);
      storeAssumeIdentity(result.token, result.expiresAt, {
        id: result.user.id,
        email: result.user.email,
        displayName: result.user.displayName,
        type: result.user.userType as 1 | 2 | 3 | 5,
        tenantId: result.user.tenantId,
        tenantName: result.user.tenantName,
        roles: result.user.roles,
        permissions: result.user.permissions,
      }, result.absoluteExpiresAt);
      window.location.reload();
    } catch (error) {
      console.error('Failed to assume identity:', error);
      toast.error('Assume Failed', 'Failed to assume identity. Please try again.');
      setAssumeTarget(undefined);
    } finally {
      setIsAssuming(false);
    }
  };

  // Map API user to UI format. Prefer the tenantName the API now ships
  // (via vsm_user_tenant JOIN — see vsm-api-v2 userService.getUserTenants);
  // fall back to the client-side tenant store only if the API was unable
  // to resolve. SuperAdmins legitimately have no tenant — render as '-',
  // not "System", so the column is unambiguous.
  const mapApiUserToUi = (apiUser: ApiUser): UserRecord => {
    const apiTenantName = (apiUser as any).tenantName as string | null | undefined;
    const fallbackTenantName = tenants.find((t) => t.id === apiUser.tenantId)?.name ?? null;
    return {
      id: apiUser.id,
      username: apiUser.username,
      email: apiUser.email || '-',
      fullName: apiUser.userRealName || apiUser.username,
      phone: apiUser.phone || '-',
      role:
        apiUser.type === USER_TYPES.SUPER_ADMIN
          ? 'SuperAdmin'
          : apiUser.type === USER_TYPES.CHANNEL_ADMIN
            ? 'ChannelAdmin'
            : apiUser.type === USER_TYPES.TENANT_ADMIN
              ? 'TenantAdmin'
              : 'User',
      type: apiUser.type,
      tenantName: apiTenantName || fallbackTenantName || '-',
      status: apiUser.status === 0 ? 'Active' : 'Inactive',
      statusValue: apiUser.status || 0,
      lastLogin: apiUser.updateTime || '-',
      createTime: apiUser.createTime || '',
    };
  };

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = { page, pageSize };

      // Filter by the topbar tenant when one is selected. /users is on the
      // interceptor's excludedPaths, so this param is not injected for us —
      // it has to be passed explicitly, for a ChannelAdmin too.
      if (isGlobalScoped && selectedTenantId) {
        params.tenantId = selectedTenantId;
      }

      const response = await userService.list(params);
      const uiUsers = response.items.map(mapApiUserToUi);
      setAllUsers(uiUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Load Failed', 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, selectedTenantId]);

  // Filter users by type for tabs
  const superAdminUsers = useMemo(
    () => allUsers.filter((u) => u.type === USER_TYPES.SUPER_ADMIN),
    [allUsers]
  );

  // First SuperAdmin (by creation time) is protected from edit/delete by others
  const firstSuperAdminId = useMemo(() => {
    if (superAdminUsers.length === 0) return null;
    const sorted = [...superAdminUsers].sort((a, b) =>
      (a.createTime || a.id).localeCompare(b.createTime || b.id)
    );
    return sorted[0]?.id;
  }, [superAdminUsers]);

  const tenantAdminUsers = useMemo(
    () => allUsers.filter((u) => u.type === USER_TYPES.TENANT_ADMIN),
    [allUsers]
  );

  const channelAdminUsers = useMemo(
    () => allUsers.filter((u) => u.type === USER_TYPES.CHANNEL_ADMIN),
    [allUsers]
  );

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleSaveUser = async (userData: any) => {
    try {
      if (editingUser) {
        await userService.update(userData.id, {
          userRealName: userData.userRealName,
          email: userData.email,
          phone: userData.phone,
          status: userData.status,
        });
        toast.success('User Updated', `User ${userData.email} updated successfully`);
      } else {
        // For new users, use the provisioning workflow which sends an invitation email
        await userService.provisionUsers({
          emails: [userData.email],
          userType: userData.type,
          tenantId: selectedTenantId || currentUser?.tenantId || '',
        });
        toast.success('User Invited', `Invitation sent to ${userData.email}`);
      }
      await fetchUsers();
      setIsAddModalOpen(false);
      setEditingUser(undefined);
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error('Save Failed', 'Failed to save user. Please try again.');
    }
  };

  const handleInviteUsers = async (data: InviteData) => {
    try {
      const response = await userService.provisionUsers({
        emails: data.emails,
        userType: data.userType,
        tenantId: data.tenantId,
        channelId: data.channelId,
        roleId: data.roleId,
        emailTemplateId: data.emailTemplateId,
        expiresInDays: data.expiresInDays,
      });

      if (response.provisioned > 0) {
        toast.success(
          'Invitations Sent',
          `Successfully invited ${response.provisioned} user${response.provisioned > 1 ? 's' : ''}`
        );
      }

      // Split failures: existing users (skipped silently with clear message) vs actual errors
      const skipped = response.results.filter((r) => r.status === 'already_exists');
      const invalid = response.results.filter((r) => r.status === 'invalid_email');
      const errors = response.results.filter((r) => r.status === 'error');

      if (skipped.length > 0) {
        const emails = skipped.map((r) => r.email).join(', ');
        toast.warning(
          'Already Active Users',
          `Skipped ${skipped.length} email${skipped.length > 1 ? 's' : ''} — already active account${skipped.length > 1 ? 's' : ''}: ${emails}`
        );
      }

      if (invalid.length > 0) {
        const emails = invalid.map((r) => r.email).join(', ');
        toast.warning(
          'Invalid Emails',
          `${invalid.length} email${invalid.length > 1 ? 's' : ''} rejected as invalid: ${emails}`
        );
      }

      if (errors.length > 0) {
        const details = errors.map((r) => `${r.email} (${r.error || 'error'})`).join(', ');
        toast.error('Some Invitations Failed', details);
      }

      // Refresh user list to show any newly provisioned users
      await fetchUsers();
    } catch (error) {
      console.error('Failed to provision users:', error);
      throw error; // Let the modal handle the error display
    }
  };

  const handleInviteVPNUsers = async (data: VPNInviteData) => {
    try {
      const response = await vpnUserService.provision({
        emails: data.emails,
        tenantId: data.tenantId,
        emailTemplateId: data.emailTemplateId,
        expiresInDays: data.expiresInDays,
        deviceGroupIds: data.deviceGroupIds,
      });

      if (response.provisioned > 0) {
        toast.success(
          'Invitations Sent',
          `Successfully invited ${response.provisioned} VPN user${response.provisioned > 1 ? 's' : ''}`
        );
      }

      const skipped = response.results.filter((r) => r.status === 'already_exists');
      const invalid = response.results.filter((r) => r.status === 'invalid_email');
      const errors = response.results.filter((r) => r.status === 'error');

      if (skipped.length > 0) {
        const emails = skipped.map((r) => r.email).join(', ');
        toast.warning(
          'Already Active Users',
          `Skipped ${skipped.length} email${skipped.length > 1 ? 's' : ''} — already active VPN user${skipped.length > 1 ? 's' : ''}: ${emails}`
        );
      }

      if (invalid.length > 0) {
        const emails = invalid.map((r) => r.email).join(', ');
        toast.warning('Invalid Emails', `${invalid.length} email${invalid.length > 1 ? 's' : ''} rejected: ${emails}`);
      }

      if (errors.length > 0) {
        const details = errors.map((r) => `${r.email} (${r.error || 'error'})`).join(', ');
        toast.error('Some Invitations Failed', details);
      }
    } catch (error) {
      console.error('Failed to provision VPN users:', error);
      throw error;
    }
  };

  const handleDeleteClick = (user: UserRecord) => {
    setDeleteUser(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUser) return;

    try {
      setIsDeleting(true);
      await userService.delete(deleteUser.id);
      toast.success('User Deleted', `User ${deleteUser.username} has been deleted`);
      await fetchUsers();
      setDeleteUser(undefined);
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Delete Failed', 'Failed to delete user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditUser = (user: UserRecord) => {
    setEditingUser(user);
  };

  // ==========================================
  // Native-auth admin actions
  // ==========================================

  const handleSetPassword = async (data: { password: string; temporary: boolean }) => {
    if (!setPwdUser) return;
    await userService.resetPassword(setPwdUser.id, { password: data.password, temporary: data.temporary });
    toast.success(
      'Password set',
      data.temporary
        ? `${setPwdUser.email} will be required to change it on next sign-in.`
        : `${setPwdUser.email} can sign in with the new password now.`
    );
    setSetPwdUser(undefined);
  };

  const handleSendResetEmail = async () => {
    if (!resetEmailUser) return;
    try {
      await userService.sendPasswordResetEmail(resetEmailUser.id);
      toast.success('Email sent', `Password reset email sent to ${resetEmailUser.email}.`);
    } catch (err: any) {
      toast.error('Failed to send', err?.response?.data?.error?.message || err?.message || 'Could not send reset email.');
    } finally {
      setResetEmailUser(undefined);
    }
  };

  const handleUnlock = async () => {
    if (!unlockUser) return;
    try {
      await userService.unlock(unlockUser.id);
      setLockedUserIds((prev) => {
        const next = new Set(prev);
        next.delete(unlockUser.id);
        return next;
      });
      toast.success('Account unlocked', `${unlockUser.email} can sign in again.`);
    } catch (err: any) {
      toast.error('Unlock failed', err?.response?.data?.error?.message || err?.message || 'Could not unlock user.');
    } finally {
      setUnlockUser(undefined);
    }
  };

  // Poll lockout status for admin rows once per list load. Best-effort — if a
  // call fails (KC unreachable, permissions), we simply don't badge that row.
  useEffect(() => {
    if (!allUsers.length) return;
    const admins = allUsers.filter((u) => u.type === USER_TYPES.SUPER_ADMIN || u.type === USER_TYPES.TENANT_ADMIN);
    let cancelled = false;
    (async () => {
      const locked = new Set<string>();
      await Promise.all(
        admins.map(async (u) => {
          try {
            const status = await userService.getLockoutStatus(u.id);
            if (status.disabled) locked.add(u.id);
          } catch {
            /* ignore */
          }
        })
      );
      if (!cancelled) setLockedUserIds(locked);
    })();
    return () => { cancelled = true; };
  }, [allUsers]);

  // Build columns - different for SuperAdmin vs TenantAdmin view
  const buildColumns = (showTenantColumn: boolean): Column<UserRecord>[] => {
    const cols: Column<UserRecord>[] = [
      {
        key: 'email',
        label: 'Email',
        sortable: true,
        render: (u) => (
          <div className="flex items-center space-x-2">
            <Mail className="w-4 h-4 text-primary-600" />
            <button
              onClick={() => navigate(`/admin/users/${u.id}`)}
              className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
            >
              {u.email !== '-' ? u.email : u.username}
            </button>
          </div>
        ),
      },
      { key: 'fullName', label: 'Name', sortable: true },
      // {
      //   key: 'role',
      //   label: 'Type',
      //   sortable: true,
      //   render: (u) => (
      //     <div className="flex items-center space-x-1">
      //       {u.role === 'SuperAdmin' ? (
      //         <Crown className="w-3 h-3 text-amber-500" />
      //       ) : (
      //         <Shield className="w-3 h-3 text-primary-600" />
      //       )}
      //       <span
      //         className={`px-2 py-1 text-xs font-medium rounded-full ${u.role === 'SuperAdmin'
      //           ? 'bg-amber-100 text-amber-700'
      //           : 'bg-primary-100 text-primary-700'
      //           }`}
      //       >
      //         {u.role === 'SuperAdmin' ? 'Super Admin' : 'Tenant Admin'}
      //       </span>
      //     </div>
      //   ),
      // },
    ];

    // Show tenant column only for SuperAdmin viewing all tenants
    if (showTenantColumn) {
      cols.push({
        key: 'tenantName',
        label: 'Tenant',
        sortable: true,
        render: (u) => {
          // SuperAdmins have no tenant association by design; render an
          // explicit "(system-wide)" hint instead of the misleading "System"
          // label that previously appeared whenever a TenantAdmin's name
          // failed to resolve from the client-side store.
          const isSystemUser = u.role === 'SuperAdmin' && (u.tenantName === '-' || !u.tenantName);
          return (
            <div className="flex items-center space-x-1">
              <Building2 className="w-3 h-3 text-gray-400" />
              <span className={`text-sm ${isSystemUser ? 'italic text-gray-500' : ''}`}>
                {isSystemUser ? '(system-wide)' : u.tenantName}
              </span>
            </div>
          );
        },
      });
    }

    cols.push(
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (u) => (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${u.status === 'Active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'
              }`}
          >
            {u.status}
          </span>
        ),
      },
      {
        key: 'lastLogin',
        label: 'Last Activity',
        render: (u) => (
          <span className="text-sm text-gray-500">
            {formatDate(u.lastLogin, useSettingsStore.getState().getDateFormatSettings())}
          </span>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (u) => {
          // First SuperAdmin is protected: other admins cannot edit/delete
          const isProtectedFirstAdmin = u.id === firstSuperAdminId && u.id !== currentUser?.id;
          // A SuperAdmin can assume a TenantAdmin or a ChannelAdmin — both are
          // console admins with a dashboard to render. Never a SuperAdmin (nothing
          // gained) or yourself; target must be Active. The API enforces the same.
          const canAssume =
            assumeEnabled &&
            (u.type === USER_TYPES.TENANT_ADMIN || u.type === USER_TYPES.CHANNEL_ADMIN) &&
            u.id !== currentUser?.id &&
            u.status === 'Active';
          const isLocked = lockedUserIds.has(u.id);
          return (
            <div className="flex justify-end space-x-2">
              {canAssume && (
                <button
                  onClick={() => setAssumeTarget(u)}
                  className="p-1.5 hover:bg-amber-50 rounded transition-colors"
                  title="Assume Identity"
                >
                  <ArrowLeftRight className="w-4 h-4 text-amber-600 hover:text-amber-700" />
                </button>
              )}
              <button
                onClick={() => navigate(`/admin/users/${u.id}`)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="View Details"
              >
                <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
              </button>
              {canUpdate && !isProtectedFirstAdmin && u.email !== '-' && (
                <button
                  onClick={() => setResetEmailUser(u)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title="Send password reset email"
                >
                  <Send className="w-4 h-4 text-gray-600 hover:text-primary-600" />
                </button>
              )}
              {canUpdate && !isProtectedFirstAdmin && u.email !== '-' && (
                <button
                  onClick={() => setSetPwdUser(u)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title="Set password"
                >
                  <KeyRound className="w-4 h-4 text-gray-600 hover:text-primary-600" />
                </button>
              )}
              {canUpdate && isLocked && (
                <button
                  onClick={() => setUnlockUser(u)}
                  className="p-1.5 hover:bg-amber-50 rounded transition-colors"
                  title="Unlock account (locked by brute-force protection)"
                >
                  <Unlock className="w-4 h-4 text-amber-600 hover:text-amber-700" />
                </button>
              )}
              {canUpdate && !isProtectedFirstAdmin && (
                <button
                  onClick={() => handleEditUser(u)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title="Edit User"
                >
                  <Edit className="w-4 h-4 text-gray-600 hover:text-primary-600" />
                </button>
              )}
              {canDelete && !isProtectedFirstAdmin && (
                <button
                  onClick={() => handleDeleteClick(u)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title="Delete User"
                  disabled={u.id === currentUser?.id}
                >
                  <Trash2
                    className={`w-4 h-4 ${u.id === currentUser?.id
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:text-red-600'
                      }`}
                  />
                </button>
              )}
            </div>
          );
        },
      }
    );

    return cols;
  };

  // Columns for different views
  const superAdminColumns = buildColumns(false); // No tenant column for SuperAdmins list
  const tenantAdminColumns = buildColumns(isSuperAdmin && !selectedTenantId); // Show tenant if viewing all

  // Stats calculation
  const stats = {
    total: allUsers.length,
    superAdmins: superAdminUsers.length,
    channelAdmins: channelAdminUsers.length,
    tenantAdmins: tenantAdminUsers.length,
    active: allUsers.filter((u) => u.status === 'Active').length,
  };

  // Render content for TenantAdmin (tabs for admins, VPN users, and invitations)
  const renderTenantAdminView = () => {
    const tenantTabs = [
      {
        id: 'admins',
        label: `Admins (${tenantAdminUsers.length})`,
        icon: Shield,
        content: (
          <DataTable
            columns={tenantAdminColumns}
            data={tenantAdminUsers}
            searchPlaceholder="Search by email or name..."
            loading={loading}
          />
        ),
      },
      {
        id: 'vpn-users',
        label: 'VPN Users',
        icon: Wifi,
        content: (
          <VPNUsersTab
            tenantId={currentUser?.tenantId ?? undefined}
            onCountChange={setVpnUserCount}
          />
        ),
      },
      {
        id: 'invitations',
        label: 'Invitations',
        icon: Clock,
        content: <PendingInvitationsTab tenantId={currentUser?.tenantId ?? undefined} />,
      },
    ];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">Manage administrators and VPN users for your tenant</p>
          </div>
          <div className="flex space-x-3">
            <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            {canCreate && (
              <button
                onClick={() => {
                  if (activeTenantTab === 'vpn-users') {
                    setIsVPNInviteModalOpen(true);
                  } else {
                    setIsInviteModalOpen(true);
                  }
                }}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <UserPlus className="w-4 h-4" />
                <span>{activeTenantTab === 'vpn-users' ? 'Invite VPN Users' : 'Invite Admins'}</span>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard icon={Shield} title="Admins" value={stats.tenantAdmins} color="primary" />
          <StatCard icon={CheckCircle} title="Active" value={stats.active} color="success" />
          <StatCard icon={Wifi} title="VPN Users" value={vpnUserCount ?? '-'} color="info" />
        </div>

        <div className="card">
          <Tabs tabs={tenantTabs} defaultTab="admins" onChange={setActiveTenantTab} />
        </div>
      </div>
    );
  };

  // Render content for the tenant-less types (SuperAdmin, ChannelAdmin).
  //
  // The selected-tenant layout is shared: it already gates Add Demo User and
  // Assume Identity on `isSuperAdmin`, so a ChannelAdmin gets the same tabs
  // without those. Only the "no tenant selected" state differs — a SuperAdmin
  // has meaningful cross-tenant tabs there, a ChannelAdmin does not (the Super
  // Admins tab would 403), so they get a prompt to pick a tenant instead.
  const renderGlobalScopedView = () => {
    // If a tenant is selected, show tabs for TenantAdmins, VPN Users, and Invitations
    if (selectedTenantId) {
      const tenantTabs = [
        {
          id: 'tenant-admins',
          label: `Tenant Admins (${tenantAdminUsers.length})`,
          icon: Shield,
          content: (
            <DataTable
              columns={buildColumns(false)}
              data={tenantAdminUsers}
              searchPlaceholder="Search by email or name..."
              loading={loading}
            />
          ),
        },
        {
          id: 'vpn-users',
          label: 'VPN Users',
          icon: Wifi,
          content: (
            <VPNUsersTab
              tenantId={selectedTenantId}
              tenantName={selectedTenant?.name}
              onCountChange={setVpnUserCount}
            />
          ),
        },
        {
          id: 'invitations',
          label: 'Invitations',
          icon: Clock,
          content: <PendingInvitationsTab tenantId={selectedTenantId} />,
        },
      ];

      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <p className="text-gray-600 mt-1">
                Managing users for{' '}
                <span className="font-semibold text-primary-600">
                  {selectedTenant?.name || 'Selected Tenant'}
                </span>
              </p>
            </div>
            <div className="flex space-x-3">
              <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => setIsDemoUserModalOpen(true)}
                  className="btn-secondary inline-flex items-center space-x-2"
                  title="Create a fully-activated demo account with a known password (no invite/email)"
                >
                  <FlaskConical className="w-4 h-4" />
                  <span>Add Demo User</span>
                </button>
              )}
              {canCreate && (
                <button
                  onClick={() => {
                    if (activeTenantTab === 'vpn-users') {
                      setIsVPNInviteModalOpen(true);
                    } else {
                      setIsInviteModalOpen(true);
                    }
                  }}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>{activeTenantTab === 'vpn-users' ? 'Invite VPN Users' : 'Invite Tenant Admins'}</span>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={Shield} title="Tenant Admins" value={tenantAdminUsers.length} color="primary" />
            <StatCard
              icon={CheckCircle}
              title="Active"
              value={tenantAdminUsers.filter((u) => u.status === 'Active').length}
              color="success"
            />
            <StatCard icon={Wifi} title="VPN Users" value={vpnUserCount ?? '-'} color="info" />
          </div>

          <div className="card">
            <Tabs tabs={tenantTabs} defaultTab="tenant-admins" onChange={setActiveTenantTab} />
          </div>
        </div>
      );
    }

    // No tenant selected, ChannelAdmin: the cross-tenant tabs below are
    // SuperAdmin-shaped (the Super Admins tab 403s, and "all tenants" for a
    // ChannelAdmin isn't a view the API offers). Point them at the picker.
    if (isChannelAdmin) {
      return (
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">Manage administrators and VPN users for your tenants</p>
          </div>
          <div className="card text-center py-12">
            <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Select a tenant to manage its users</p>
            <p className="text-gray-500 text-sm mt-1">
              Pick one from the tenant selector in the top bar, or open a channel to choose from
              its tenants.
            </p>
            <button onClick={() => navigate('/admin/channels')} className="btn-secondary mt-4">
              Go to Channels
            </button>
          </div>
        </div>
      );
    }

    // No tenant selected - show tabs for Super Admins, Tenant Admins, and VPN Users
    const tabs = [
      {
        id: 'super-admins',
        label: `Super Admins (${superAdminUsers.length})`,
        icon: Crown,
        content: (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Super Administrators</strong> have full system access across all tenants.
                They can manage users, tenants, and system settings.
              </p>
            </div>
            <DataTable
              columns={superAdminColumns}
              data={superAdminUsers}
              searchPlaceholder="Search super admins..."
              loading={loading}
            />
          </div>
        ),
      },
      {
        id: 'channel-admins',
        label: `Channel Admins (${channelAdminUsers.length})`,
        icon: Network,
        content: (
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <p className="text-sm text-indigo-800">
                <strong>Channel Administrators</strong> manage the tenants inside the channels they
                administer, without gaining global access. They belong to no single tenant.
              </p>
              <p className="text-xs text-indigo-700 mt-2">
                Inviting one here creates the account. Granting it a channel is a separate step —
                use the <strong>Administrators</strong> tab on the channel itself. Only a Super
                Admin can do either.
              </p>
            </div>
            <DataTable
              columns={superAdminColumns}
              data={channelAdminUsers}
              searchPlaceholder="Search channel admins..."
              loading={loading}
            />
          </div>
        ),
      },
      {
        id: 'tenant-admins',
        label: `Tenant Admins (${tenantAdminUsers.length})`,
        icon: Shield,
        content: (
          <div className="space-y-4">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-center justify-between">
              <p className="text-sm text-primary-800">
                <strong>Tenant Administrators</strong> can manage resources within their assigned
                tenant. Select a tenant from the dropdown above to invite new admins.
              </p>
              {!selectedTenantId && (
                <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded">
                  Select tenant to invite
                </span>
              )}
            </div>
            <DataTable
              columns={tenantAdminColumns}
              data={tenantAdminUsers}
              searchPlaceholder="Search tenant admins..."
              loading={loading}
            />
          </div>
        ),
      },
      {
        id: 'vpn-users',
        label: 'VPN Users',
        icon: Wifi,
        content: (
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-800">
                <strong>VPN Users</strong> are client users who connect via the VPN application.
                Select a tenant from the dropdown above to manage VPN users for that tenant.
              </p>
            </div>
            <VPNUsersTab />
          </div>
        ),
      },
      {
        id: 'invitations',
        label: 'Invitations',
        icon: Clock,
        content: (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-800">
                <strong>Pending Invitations</strong> shows all user invitations across tenants.
                Select a tenant from the dropdown above to filter by tenant.
              </p>
            </div>
            <PendingInvitationsTab />
          </div>
        ),
      },
    ];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">
              Manage system and tenant administrators
              {!selectedTenantId && (
                <span className="text-xs ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded">
                  Viewing all tenants
                </span>
              )}
            </p>
          </div>
          <div className="flex space-x-3">
            <button className="btn-secondary inline-flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            {isSuperAdmin && (
              <button
                onClick={() => setIsDemoUserModalOpen(true)}
                className="btn-secondary inline-flex items-center space-x-2"
                title="Create a fully-activated demo account with a known password (no invite/email)"
              >
                <FlaskConical className="w-4 h-4" />
                <span>Add Demo User</span>
              </button>
            )}
            {canCreate && (
              <button
                onClick={() => {
                  if (activeSuperAdminTab === 'vpn-users') {
                    setIsVPNInviteModalOpen(true);
                  } else {
                    setIsInviteModalOpen(true);
                  }
                }}
                className={`inline-flex items-center space-x-2 ${canInvite ? 'btn-primary' : 'btn-secondary opacity-50 cursor-not-allowed'
                  }`}
                disabled={!canInvite}
                title={
                  !canInvite
                    ? `Select a tenant in the topbar to invite ${activeSuperAdminTab === 'vpn-users' ? 'VPN users' : 'tenant admins'}`
                    : ''
                }
              >
                <UserPlus className="w-4 h-4" />
                <span>
                  {activeSuperAdminTab === 'vpn-users'
                    ? 'Invite VPN Users'
                    : activeSuperAdminTab === 'super-admins'
                      ? 'Invite Super Admins'
                      : activeSuperAdminTab === 'channel-admins'
                        ? 'Invite Channel Admins'
                        : 'Invite Tenant Admins'}
                </span>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <StatCard icon={Users} title="Total Users" value={stats.total} color="primary" />
          <StatCard icon={Crown} title="Super Admins" value={stats.superAdmins} color="warning" />
          <StatCard icon={Network} title="Channel Admins" value={stats.channelAdmins} color="secondary" />
          <StatCard icon={Shield} title="Tenant Admins" value={stats.tenantAdmins} color="info" />
          <StatCard icon={CheckCircle} title="Active" value={stats.active} color="success" />
        </div>

        <div className="card">
          <Tabs tabs={tabs} defaultTab="tenant-admins" onChange={setActiveSuperAdminTab} />
        </div>
      </div>
    );
  };

  return (
    <>
      {isGlobalScoped ? renderGlobalScopedView() : renderTenantAdminView()}

      {/* Add/Edit User Modal (legacy - for direct creation) */}
      <UserModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveUser}
        mode="add"
      />

      <UserModal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(undefined)}
        onSave={handleSaveUser}
        user={
          editingUser
            ? {
              id: editingUser.id,
              username: editingUser.username,
              email: editingUser.email,
              userRealName: editingUser.fullName,
              phone: editingUser.phone,
              type: editingUser.type,
              status: editingUser.statusValue,
              tenantId: editingUser.tenantName,
            }
            : undefined
        }
        mode="edit"
      />

      {/* Invite Admin Users Modal (Super Admin or Tenant Admin) */}
      {/* Only a SuperAdmin can reach the super-admin and channel-admin variants:
          both branches are guarded on isSuperAdmin, and the server refuses
          userType 1 and 5 from anyone below regardless. */}
      <InviteUsersModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteUsers}
        inviteType={
          isSuperAdmin && activeSuperAdminTab === 'super-admins'
            ? 'super-admin'
            : isSuperAdmin && activeSuperAdminTab === 'channel-admins'
              ? 'channel-admin'
              : 'tenant-admin'
        }
        tenantId={
          isSuperAdmin && activeSuperAdminTab === 'channel-admins'
            ? ''
            : selectedTenantId || currentUser?.tenantId || ''
        }
        tenantName={selectedTenant?.name || 'Your Tenant'}
      />

      {/* Invite VPN Users Modal */}
      <InviteVPNUsersModal
        isOpen={isVPNInviteModalOpen}
        onClose={() => setIsVPNInviteModalOpen(false)}
        onInvite={handleInviteVPNUsers}
        tenantId={selectedTenantId || currentUser?.tenantId || ''}
        tenantName={selectedTenant?.name || 'Your Tenant'}
      />

      {/* Add Demo User Modal (SuperAdmin only) */}
      {isSuperAdmin && (
        <AddDemoUserModal
          isOpen={isDemoUserModalOpen}
          onClose={() => setIsDemoUserModalOpen(false)}
          onCreated={fetchUsers}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteUser}
        onClose={() => setDeleteUser(undefined)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete user "${deleteUser?.username}"?\n\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Assume Identity Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!assumeTarget}
        onClose={() => setAssumeTarget(undefined)}
        onConfirm={handleAssumeConfirm}
        title="Assume Identity"
        message={`You are about to view the UI as "${assumeTarget?.email}"${
          assumeTarget?.type === USER_TYPES.CHANNEL_ADMIN
            ? ' (Channel Admin)'
            : assumeTarget?.tenantName && assumeTarget.tenantName !== '-'
              ? ` (${assumeTarget.tenantName})`
              : ''
        }.\n\nYou will see exactly what this user sees. A banner will allow you to return to your admin account.`}
        confirmText="Assume Identity"
        cancelText="Cancel"
        variant="warning"
        isLoading={isAssuming}
      />

      {/* Set Password Modal */}
      <SetPasswordModal
        isOpen={!!setPwdUser}
        onClose={() => setSetPwdUser(undefined)}
        onSave={handleSetPassword}
        userEmail={setPwdUser?.email || setPwdUser?.username || ''}
      />

      {/* Send Reset Email Confirmation */}
      <ConfirmDialog
        isOpen={!!resetEmailUser}
        onClose={() => setResetEmailUser(undefined)}
        onConfirm={handleSendResetEmail}
        title="Send password reset email"
        message={`Send a password reset email to ${resetEmailUser?.email}? The user will get a one-time link to set a new password.`}
        confirmText="Send email"
        cancelText="Cancel"
      />

      {/* Unlock Confirmation */}
      <ConfirmDialog
        isOpen={!!unlockUser}
        onClose={() => setUnlockUser(undefined)}
        onConfirm={handleUnlock}
        title="Unlock account"
        message={`Clear the brute-force lockout on ${unlockUser?.email}? They'll be able to sign in again immediately.`}
        confirmText="Unlock"
        cancelText="Cancel"
      />
    </>
  );
};

export default UsersAdmin;
